import { useState, useEffect, useRef } from 'react';
import { useCxContentDetail, useCxMutations } from '@hooks/useCallExperience';
import { parseApiError } from '@utils/errorUtils';
import { Highlighted, OriginBadge, fillSample, speak, mentionedKeys, acceptedKeys, useCxPrefs } from './cxFormat';

const KIND_LABELS = { builtin: 'Built-in line', template: 'Message', question: 'IVR question' };
const JOURNEY_NAMES = { inbound: 'Inbound call', 'outbound-coach': 'Outbound coach call', 'resupply-ivr': 'Resupply IVR' };

/** Warns when wording tells callers to press a key the step won't accept (a warning only; saving still works). */
function KeyWarning({ text, accepted }) {
  if (!accepted) return null;
  const stray = mentionedKeys(text).filter((k) => !accepted.includes(k));
  if (stray.length === 0) return null;
  return (
    <div className="cx-callout cx-callout-warn cx-mt" role="status">
      This wording mentions {stray.join(', ')}, but callers can only press {accepted.join(', ')} here.
    </div>
  );
}

const LISTEN_TITLE = "Plays in your browser's voice, not the voice used on the call";

/** English + Spanish textareas with placeholder buttons, a live preview and "Listen". */
function TextEditor({ en, es, onChange, placeholders, lang, companyName, accepted }) {
  const enRef = useRef(null);
  const insert = (token) => {
    const el = enRef.current;
    const start = el?.selectionStart ?? en.length;
    const end = el?.selectionEnd ?? en.length;
    onChange({ en: en.slice(0, start) + token + en.slice(end), es });
  };
  const previewText = lang === 'es' ? (es.trim() || en) : en;
  return (
    <>
      <div className="cx-dsec">
        <label className="cx-dl" htmlFor="cx-ed-en">English</label>
        <textarea id="cx-ed-en" ref={enRef} value={en} onChange={(e) => onChange({ en: e.target.value, es })} />
        <KeyWarning text={en} accepted={accepted} />
        {placeholders.length > 0 && (
          <div className="cx-phbtns">
            {placeholders.map((p) => (
              <button key={p} type="button" onClick={() => insert(`{${p}}`)}>{`{${p}}`}</button>
            ))}
          </div>
        )}
      </div>
      <div className="cx-dsec">
        <label className="cx-dl" htmlFor="cx-ed-es">
          Spanish <span className="cx-muted">(blank = Spanish callers hear the English above)</span>
        </label>
        <textarea id="cx-ed-es" value={es} onChange={(e) => onChange({ en, es: e.target.value })} />
        <KeyWarning text={es} accepted={accepted} />
      </div>
      <div className="cx-dsec">
        <div className="cx-dl">
          Preview{companyName ? ` (${companyName})` : ' (sample company)'}
          <button type="button" className="cx-btn cx-btn-small" title={LISTEN_TITLE} onClick={() => speak(fillSample(en, companyName), 'en')}>▶ English</button>
          <button type="button" className="cx-btn cx-btn-small" title={LISTEN_TITLE} onClick={() => speak(fillSample(es.trim() || en, companyName), 'es')}>▶ Spanish</button>
        </div>
        <div className="cx-preview">{previewText.trim() ? fillSample(previewText, companyName) : <i>silent</i>}</div>
        <div className="cx-when cx-block">Listen plays the words in your browser&apos;s voice. It is not the voice patients hear on the call (Google voices), so it will sound different.</div>
      </div>
    </>
  );
}

function CompanyList({ title, rows, empty, onOpen }) {
  return (
    <div className="cx-dsec">
      <div className="cx-dl">{title} ({rows.length})</div>
      {rows.length ? (
        <div className="cx-custlist">
          {rows.map((r) => (
            <div key={r.companyId} className="cx-custrow">
              <span className="cx-who">
                {r.isParent ? '⬡ ' : ''}{r.name}
                {r.isParent && <span className="cx-when"> {r.memberCount} companies</span>}
              </span>
              <span className="cx-snip">{r.en || ''}</span>
              <button type="button" className="cx-btn cx-btn-small" onClick={() => onOpen(r.companyId)}>Open</button>
            </div>
          ))}
        </div>
      ) : <span className="cx-muted">{empty}</span>}
    </div>
  );
}

function VariantRow({ variant, onSave, onRemove, busy }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState({ en: variant.en, es: variant.es || '' });
  return (
    <div className="cx-custrow cx-variant">
      <span className="cx-who">{variant.patientTypeName}</span>
      {editing ? (
        <div className="cx-variant-edit">
          <textarea value={text.en} onChange={(e) => setText({ ...text, en: e.target.value })} aria-label="English" />
          <textarea value={text.es} onChange={(e) => setText({ ...text, es: e.target.value })} aria-label="Spanish" placeholder="Spanish (optional)" />
          <div className="cx-row">
            <button type="button" className="cx-btn cx-btn-small cx-btn-primary" disabled={busy || !text.en.trim()} onClick={() => onSave(variant, text).then(() => setEditing(false))}>Save</button>
            <button type="button" className="cx-btn cx-btn-small" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <span className="cx-snip">{variant.en}</span>
          <button type="button" className="cx-btn cx-btn-small" onClick={() => setEditing(true)}>Edit</button>
          <button type="button" className="cx-btn cx-btn-small cx-btn-danger" disabled={busy} onClick={() => onRemove(variant)}>Remove</button>
        </>
      )}
    </div>
  );
}

/**
 * The editor for one line in the current viewing context. Global: edit the default and see who
 * deviates. Company or group: see what is inherited, customize, or remove own wording. Built-ins
 * and parent-group questions are read-only.
 */
export default function ContentDrawer({ line, ctx, lang, companies, onClose, onSwitchContext, onGoToJourney, onNotify, startEditing }) {
  const { technical } = useCxPrefs();
  const hasIdentifier = !line.key.startsWith('#');
  const { data: detail, isLoading, error } = useCxContentDetail(hasIdentifier ? line : null, ctx.companyId);
  const { save, remove, saveVariant, removeVariant, toggle } = useCxMutations();
  const [editing, setEditing] = useState(!!startEditing);
  const [text, setText] = useState(null);
  const [custQuery, setCustQuery] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Seed the editor once the drawer data arrives: own wording, else what is inherited, else the default.
  useEffect(() => {
    if (!detail || text) return;
    const source = detail.own || (ctx.companyId ? detail.inherited : null) || detail.global;
    setText({ en: source?.en || '', es: source?.es || '' });
  }, [detail, ctx.companyId, text]);

  const busy = save.isPending || remove.isPending || saveVariant.isPending || removeVariant.isPending || toggle.isPending;
  const params = { kind: line.kind, key: line.key, productLineId: line.productLineId, companyId: ctx.companyId || null };

  const run = async (mutation, variables, success) => {
    try {
      await mutation.mutateAsync(variables);
      onNotify('success', success);
      return true;
    } catch (err) {
      onNotify('error', parseApiError(err));
      return false;
    }
  };

  const onSave = async () => {
    const ok = await run(save, { ...params, en: text.en.trim(), es: text.es.trim() || null },
      ctx.companyId ? `Saved for ${ctx.name}` : 'Saved the global default');
    if (ok) setEditing(false);
  };

  const onRemove = async () => {
    const ok = await run(remove, params, `${ctx.name} now uses the inherited wording`);
    if (ok) { setEditing(false); setText(null); }
  };

  const ctxLabel = ctx.companyId ? `${ctx.isParent ? '⬡' : '🏢'} ${ctx.name}` : '🌐 Global default';
  const placeholders = detail?.placeholders || line.placeholders || [];
  const accepted = acceptedKeys(detail?.usage);
  const custHits = custQuery.trim()
    ? companies.filter((c) => (c.name.toLowerCase().includes(custQuery.trim().toLowerCase()) || String(c.id).includes(custQuery.trim()))
      && !(c.isParent && line.kind === 'question')).slice(0, 8)
    : [];

  let body;
  let foot = <><span className="cx-spacer" /><button type="button" className="cx-btn" onClick={onClose}>Close</button></>;

  if (!hasIdentifier) {
    body = (
      <div className="cx-dsec cx-callout cx-callout-warn">
        This question has no identifier, so companies can&apos;t customize it and only a developer can change its wording
        {technical ? <> (in <code>S3_OrderQuestions</code>)</> : null}.
        <div className="cx-preview cx-mt"><Highlighted text={line.text} /></div>
      </div>
    );
  } else if (isLoading || !detail || !text) {
    body = error ? <div className="cx-error">{parseApiError(error)}</div> : <div className="cx-muted">Loading…</div>;
  } else if (line.kind === 'builtin') {
    body = (
      <>
        <div className="cx-dsec cx-lockbox">🔒 Built into the code{technical && <> (<code>{detail.builtinSource}</code>)</>}. Every company hears the same wording, and changing it needs a release.</div>
        <div className="cx-dsec"><div className="cx-dl">English</div><div className="cx-preview"><Highlighted text={detail.global.en} /></div></div>
        <div className="cx-dsec"><div className="cx-dl">Spanish</div>
          <div className="cx-preview">{detail.global.es ? <Highlighted text={detail.global.es} /> : <i>English only: Spanish callers hear English</i>}</div></div>
      </>
    );
  } else if (!ctx.companyId) {
    body = (
      <>
        <div className="cx-dsec cx-dl">Global default: every company hears this unless it has its own wording</div>
        {line.required && <div className="cx-dsec"><span className="cx-badge cx-badge-req">required: always asked</span></div>}
        <TextEditor {...text} onChange={setText} placeholders={placeholders} lang={lang} accepted={accepted} />
        <CompanyList title="Customized wording" rows={detail.customizers} empty="No company has its own wording; all use the default."
          onOpen={(id) => onSwitchContext(id)} />
        {line.kind === 'question' && (
          <CompanyList title="Turned off for" rows={detail.disablers} empty="On for every company." onOpen={(id) => onSwitchContext(id)} />
        )}
        <div className="cx-dsec">
          <label className="cx-dl" htmlFor="cx-cust-search">Customize for a {line.kind === 'question' ? 'company' : 'company or group'}</label>
          <input id="cx-cust-search" type="text" className="cx-input-full" value={custQuery}
            placeholder="Search companies by name or ID…" onChange={(e) => setCustQuery(e.target.value)} />
          {custQuery.trim() && (
            <div className="cx-results">
              {custHits.length ? custHits.map((c) => (
                <button key={c.id} type="button" onClick={() => onSwitchContext(String(c.id), { editing: true })}>
                  {c.isParent ? '⬡' : '🏢'} {c.name} <span className="cx-muted">· {c.id}</span>
                </button>
              )) : <div className="cx-muted cx-pad">No matches</div>}
            </div>
          )}
        </div>
      </>
    );
    foot = (
      <>
        <span className="cx-spacer" />
        <button type="button" className="cx-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="cx-btn cx-btn-primary" disabled={busy || !text.en.trim()} onClick={onSave}>Save global default</button>
      </>
    );
  } else if (detail.globalOnly) {
    body = (
      <>
        <div className="cx-dsec cx-callout">
          This line is spoken before the call is tied to a company, so it applies to every company and can only be changed
          as the global default. {ctx.name} hears:
        </div>
        <div className="cx-dsec cx-preview"><Highlighted text={lang === 'es' ? (detail.global.es || detail.global.en) : detail.global.en} /></div>
        <div className="cx-dsec"><button type="button" className="cx-btn" onClick={() => onSwitchContext('')}>Edit the global default</button></div>
      </>
    );
  } else if (ctx.isParent && line.kind === 'question') {
    body = (
      <>
        <div className="cx-dsec cx-callout cx-callout-warn">IVR questions can only be customized per company, not for a parent group. Members of {ctx.name} hear:</div>
        <div className="cx-dsec cx-preview"><Highlighted text={detail.inherited?.en || detail.global.en} /></div>
        <div className="cx-dsec"><button type="button" className="cx-link" onClick={() => onSwitchContext('')}>Edit the global default instead</button></div>
      </>
    );
  } else {
    const inherited = detail.inherited;
    const showEditor = !!detail.own || editing;
    body = (
      <>
        {line.toggleable && ctx.isCompany && (
          <div className="cx-dsec cx-dl">
            Ask this question for {ctx.name}
            <button type="button" role="switch" aria-checked={line.enabled} className={`cx-sw${line.enabled ? ' cx-sw-on' : ''}`} disabled={busy}
              onClick={() => run(toggle, { identifier: line.key, companyId: ctx.companyId, enabled: !line.enabled },
                `${line.label} turned ${line.enabled ? 'off' : 'on'} for ${ctx.name}`).then(onClose)} />
            <span>{line.enabled ? 'On' : 'Off'}</span>
          </div>
        )}
        {line.required && <div className="cx-dsec"><span className="cx-badge cx-badge-req">required: always asked</span></div>}
        {showEditor ? (
          <>
            <div className="cx-dsec cx-dl">{ctx.name}&apos;s own wording{ctx.isParent ? ' (applies to every member company without its own)' : ''}</div>
            <TextEditor {...text} onChange={setText} placeholders={placeholders} lang={lang} companyName={ctx.isCompany ? ctx.name : null} accepted={accepted} />
            <div className="cx-dsec">
              <div className="cx-dl">If removed, {ctx.name} goes back to: {inherited.label}</div>
              <div className="cx-inherit">{inherited.en ? <Highlighted text={inherited.en} /> : <i>silent</i>}</div>
            </div>
          </>
        ) : (
          <>
            <div className="cx-dsec">
              <div className="cx-dl">{ctx.name} uses: {inherited.label}</div>
              <div className="cx-inherit">{inherited.en ? <Highlighted text={inherited.en} /> : <i>silent</i>}</div>
            </div>
            <div className="cx-dsec cx-row">
              <button type="button" className="cx-btn cx-btn-primary" onClick={() => setEditing(true)}>Customize for {ctx.name}</button>
              <button type="button" className="cx-btn" onClick={() => onSwitchContext(inherited.contextId ? String(inherited.contextId) : '')}>
                Edit {inherited.label} instead
              </button>
            </div>
          </>
        )}
      </>
    );
    if (showEditor) {
      foot = (
        <>
          {detail.own && <button type="button" className="cx-btn cx-btn-danger" disabled={busy} onClick={onRemove}>Remove custom wording</button>}
          <span className="cx-spacer" />
          <button type="button" className="cx-btn" onClick={() => (detail.own ? onClose() : setEditing(false))}>Cancel</button>
          <button type="button" className="cx-btn cx-btn-primary" disabled={busy || !text.en.trim()} onClick={onSave}>Save for {ctx.name}</button>
        </>
      );
    }
  }

  return (
    <aside className="cx-drawer" aria-label={`Edit ${line.label}`}>
      <header>
        <div>
          <div className="cx-muted cx-small">{ctxLabel}</div>
          <h3>{line.label}</h3>
          {technical && <code className="cx-muted">{line.key} · </code>}
          <span className="cx-muted cx-small">{KIND_LABELS[line.kind]}</span>
          <div className="cx-mt"><OriginBadge origin={line.origin} /></div>
        </div>
        <button type="button" className="cx-x" onClick={onClose} aria-label="Close">✕</button>
      </header>
      <div className="cx-dbody">
        {body}
        {detail && line.kind === 'template' && detail.patientTypeVariants.length > 0 && (
          <div className="cx-dsec">
            <div className="cx-dl">Patient-type variants (win over the wording above for that patient type)</div>
            <div className="cx-custlist">
              {detail.patientTypeVariants.map((v) => (
                <VariantRow key={v.id} variant={v} busy={busy}
                  onSave={(variant, t) => run(saveVariant, { key: line.key, id: variant.id, en: t.en.trim(), es: t.es.trim() || null }, `Saved the ${variant.patientTypeName} variant`)}
                  onRemove={(variant) => run(removeVariant, { key: line.key, id: variant.id }, `Removed the ${variant.patientTypeName} variant`)} />
              ))}
            </div>
          </div>
        )}
        {detail && (
          <div className="cx-dsec">
            <div className="cx-dl">Used in</div>
            <div className="cx-used">
              {detail.usage.length ? detail.usage.map((u) => (
                <button key={`${u.journeyId}:${u.productLineId}:${u.stepId}`} type="button" className="cx-link" onClick={() => onGoToJourney(u)}>
                  {technical ? u.journeyId : JOURNEY_NAMES[u.journeyId] || u.journeyId}{u.productLineId ? ` (${u.productLineId === 2 ? 'CGM' : 'CPAP'})` : ''} · {u.stepTitle}
                </button>
              )) : <span className="cx-muted">Not placed in a journey</span>}
            </div>
            {accepted && <div className="cx-when cx-block">Callers can press: {accepted.join(', ')}</div>}
            {detail.sharedWith.length > 0 && (
              <div className="cx-when cx-block">
                A company&apos;s wording also applies to: {detail.sharedWith.map((s) => `${s.label} (${s.productLineId === 2 ? 'CGM' : 'CPAP'})`).join(', ')}
              </div>
            )}
          </div>
        )}
        {detail?.note && <div className="cx-dsec cx-callout cx-callout-warn">{detail.note}</div>}
      </div>
      <div className="cx-dfoot">{foot}</div>
    </aside>
  );
}
